import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Toast from './Toast';
import '../styles/UnifiedAuth.css';
import { API_URL } from '../api/config';

export default function OutletSelection() {
  const location = useLocation();
  const { outlets = [], email: passedEmail = '' } = location.state || {};
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [password, setPassword] = useState('');
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [attemptCount, setAttemptCount] = useState(0);
  const { user, signIn: authSignIn } = useAuth();
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const closeToast = () => {
    setToast({ ...toast, show: false });
  };

  // Use the authenticated user's email if available; otherwise, use the passed email.
  const currentEmail = user?.email || passedEmail;

  useEffect(() => {
    if (outlets.length === 0) {
      showToast('No outlets available. Please try again.', 'error');
      setTimeout(() => navigate('/signin', { replace: true }), 1500);
      return;
    }
    
    if (outlets.length === 1) {
      setSelectedOutlet(outlets[0]);
      checkPreviousLogin(outlets[0]);
    }
  }, [outlets, navigate]);

  const checkPreviousLogin = async (outlet) => {
    try {
      const savedAccounts = localStorage.getItem('savedAccounts');
      if (savedAccounts) {
        const accounts = JSON.parse(savedAccounts);
        const existingAccount = accounts.find(
          account => account.restaurantId === outlet.restaurantId && account.email === currentEmail
        );
        if (existingAccount) {
          handleAutoLogin(outlet);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking previous login:', error);
      return false;
    }
  };

  const handleAutoLogin = async (outlet) => {
    try {
      setLoading(true);
      
      const response = await axios.post(`${API_URL}/auto-login`, {
        email: currentEmail,
        restaurantId: outlet.restaurantId
      });

      if (response.data.success) {
        const { token, user: apiUser } = response.data;
        
        const account = {
          email: currentEmail,
          restaurantId: outlet.restaurantId,
          restaurantName: outlet.restaurantName,
          outletName: outlet.outletName
        };
        
        let savedAccounts = JSON.parse(localStorage.getItem('savedAccounts') || '[]');
        if (!Array.isArray(savedAccounts)) savedAccounts = [];
        if (!savedAccounts.some(a => a.email === currentEmail && a.restaurantId === outlet.restaurantId)) {
          savedAccounts.push(account);
          localStorage.setItem('savedAccounts', JSON.stringify(savedAccounts));
        }

        await authSignIn({ token, user: apiUser });
        showToast('Signed in successfully');
        setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
      }
    } catch (error) {
      console.error('Auto-login error:', error);
      setShowPasswordInput(true);
    } finally {
      setLoading(false);
    }
  };

  const handleOutletSelect = async () => {
    if (!selectedOutlet) {
      showToast('Please select an outlet', 'error');
      return;
    }
    const isPreviouslyLoggedIn = await checkPreviousLogin(selectedOutlet);
    if (!isPreviouslyLoggedIn) {
      setShowPasswordInput(true);
    }
    setErrorMessage('');
  };

  const handlePasswordSubmit = async () => {
    if (!password) {
      setErrorMessage('Please enter your password');
      return;
    }
    try {
      setLoading(true);
      setErrorMessage('');
      
      const response = await axios.post(`${API_URL}/select-outlet`, {
        email: currentEmail,
        restaurantId: selectedOutlet.restaurantId,
        password
      });

      if (response.data.success) {
        const { token, user: apiUser } = response.data;
        
        const account = {
          email: currentEmail,
          restaurantId: selectedOutlet.restaurantId,
          restaurantName: selectedOutlet.restaurantName,
          outletName: selectedOutlet.outletName
        };
        
        let savedAccounts = JSON.parse(localStorage.getItem('savedAccounts') || '[]');
        if (!Array.isArray(savedAccounts)) savedAccounts = [];
        if (!savedAccounts.some(a => a.email === currentEmail && a.restaurantId === selectedOutlet.restaurantId)) {
          savedAccounts.push(account);
          localStorage.setItem('savedAccounts', JSON.stringify(savedAccounts));
        }

        await authSignIn({ token, user: apiUser });
        showToast('Signed in successfully');
        setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
      }
    } catch (error) {
      console.log('Outlet selection error:', error);
      setAttemptCount(prev => prev + 1);
      if (error.response?.status === 429) {
        const message = 'Too many attempts. Please try again later.';
        setErrorMessage(message);
        showToast(message, 'error');
        setTimeout(() => navigate(-1), 1500);
      } else if (error.response?.status === 401) {
        setErrorMessage('Incorrect password. Please try again.');
        setPassword('');
      } else {
        const message = error.response?.data?.message || 'An error occurred while selecting outlet';
        setErrorMessage(message);
        showToast(message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="unified-auth-container">
      {toast.show && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}
      <div className="auth-form-wrapper">
        <h1 style={{ textAlign: 'center', color: '#8B4513', marginBottom: '5px' }}>TOBO</h1>
        <p style={{ textAlign: 'center', color: '#CD853F', marginBottom: '20px' }}>BILL IT, DONE!</p>
        {!showPasswordInput ? (
          <>
            <p style={{ textAlign: 'center', marginBottom: '20px' }}>
              Select the outlet you want to sign in to
            </p>
            <div style={{ marginBottom: '20px' }}>
              {outlets.map((item) => (
                <div
                  key={item.restaurantId}
                  style={{
                    padding: '15px',
                    marginBottom: '10px',
                    borderRadius: '10px',
                    border: selectedOutlet?.restaurantId === item.restaurantId ? '2px solid #8B4513' : '1px solid #E9967A',
                    cursor: 'pointer',
                    backgroundColor: 'rgba(255, 255, 255, 0.5)'
                  }}
                  onClick={() => setSelectedOutlet(item)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ fontWeight: 'bold', color: '#8B4513' }}>{item.restaurantName}</h3>
                      <p style={{ color: '#CD853F', fontSize: '14px' }}>{item.outletName}</p>
                      <p style={{ color: '#777', fontSize: '12px' }}>ID: {item.restaurantId}</p>
                    </div>
                    {selectedOutlet?.restaurantId === item.restaurantId && (
                      <div style={{ color: '#8B4513', fontWeight: 'bold' }}>✓</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button
              className="submit-btn"
              onClick={handleOutletSelect}
              disabled={!selectedOutlet || loading}
              style={{ opacity: !selectedOutlet || loading ? 0.7 : 1 }}
            >
              Continue
            </button>
          </>
        ) : (
          <>
            <p style={{ textAlign: 'center', marginBottom: '20px' }}>
              Enter password for {selectedOutlet.restaurantName} - {selectedOutlet.outletName}
            </p>
            <div className="form-group">
              <div style={{ position: 'relative' }}>
                <Lock style={{ position: 'absolute', left: '12px', top: '12px', color: '#8B4513' }} size={20} />
                <input
                  style={{ paddingLeft: '40px' }}
                  placeholder="Enter your password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errorMessage) setErrorMessage('');
                  }}
                  autoFocus
                />
              </div>
            </div>
            {errorMessage && (
              <p className="error-message">{errorMessage}</p>
            )}
            <button
              className="submit-btn"
              onClick={handlePasswordSubmit}
              disabled={!password || loading || attemptCount >= 5}
              style={{ opacity: !password || loading || attemptCount >= 5 ? 0.7 : 1 }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <button
              className="auth-links"
              onClick={() => setShowPasswordInput(false)}
              style={{ 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer', 
                marginTop: '15px',
                display: 'flex',
                alignItems: 'center',
                color: '#8B4513'
              }}
            >
              <ChevronLeft size={16} />
              <span>Back to outlet selection</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
